import React, {
    useRef,
    useState,
    useEffect,
    useLayoutEffect,
    useCallback,
} from "react";

import { motion } from "framer-motion";

import gsap from "gsap";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import "./ServiceShowcase.css";

import gridCompliance from "../assets/grid-compliance-study.png";
import evacuationStudy from "../assets/evacuation-study.jpg";
import protectionStudy from "../assets/protection-study.png";
import optimization from "../assets/optimization.jpg";
import software from "../assets/software.jpg";
import technicalConsultant from "../assets/technical-consultant.jpg";
import planning from "../assets/planning.jpg";
import pqWave from "../assets/pq-wave.jpg";
import powerSystemConsultancy from "../assets/power-system-consultancy.jpg";
import substationDesign from "../assets/substation-design.jpg";

const SERVICES = [
    {
        title: "Grid Compliance Study",
        detailId: "grid-compliance",
        image: gridCompliance,
        description:
            "Comprehensive grid compliance studies ensuring reliable and regulation-compliant power system operation.",
    },
    {
        title: "Evacuation Study",
        detailId: "evacuation-study",
        image: evacuationStudy,
        description:
            "Transmission evacuation planning for renewable and conventional generation projects.",
    },
    {
        title: "Protection Study",
        detailId: "protection-study",
        image: protectionStudy,
        description:
            "Protection coordination, relay settings and fault analysis for secure system operation.",
    },
    {
        title: "Optimization Study",
        detailId: "wind-solar-optimization",
        image: optimization,
        description:
            "Power system optimization to improve efficiency, stability and network performance.",
    },
    {
        title: "Software Development",
        detailId: "software-development",
        image: software,
        description:
            "Custom engineering software and digital solutions for utilities and power industries.",
    },
    {
        title: "Technical Consultancy",
        detailId: "technical-consultancy",
        image: technicalConsultant,
        description:
            "Expert technical consultancy for power system planning, design and execution.",
    },
    {
        title: "Planning Study",
        detailId: "transmission-planning",
        image: planning,
        description:
            "Long-term transmission and distribution planning studies for future-ready networks.",
    },
    {
        title: "Power Quality",
        detailId: "power-quality",
        image: pqWave,
        description:
            "Power quality assessment, harmonics analysis and waveform monitoring services.",
    },
    {
        title: "Power System Consultancy",
        detailId: "power-system-consultancy",
        image: powerSystemConsultancy,
        description:
            "Complete consultancy services covering analysis, engineering and project support.",
    },
    {
        title: "Substation Design",
        detailId: "substation-design",
        image: substationDesign,
        description:
            "Comprehensive AIS/GIS substation engineering and detailed drawings.",
    },
];

// Responsive card count. Matches the project's existing tiers:
// phone / tablet / small-laptop / large-desktop.
const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;
const DESKTOP_BREAKPOINT = 1440;

const getCardsPerPage = () => {

    if (typeof window === "undefined") {
        return 5;
    }

    const width = window.innerWidth;

    if (width <= MOBILE_BREAKPOINT) return 2;
    if (width <= TABLET_BREAKPOINT) return 3;
    if (width <= DESKTOP_BREAKPOINT) return 4;

    return 5;
};

function ServiceCard({ service }) {


    return (
        <div className="service-card">

            <div className="service-card-image">

                <img
                    src={service.image}
                    alt={service.title}
                    loading="lazy"
                />

            </div>

            <div className="service-card-content">

                <h3>{service.title}</h3>

                <p>{service.description}</p>
                <Link
                    to={`/services#${service.detailId}`}
                    className="service-read-more"
                >
                    Read More
                    <span className="service-read-more-arrow">→</span>
                </Link>

            </div>

        </div>
    );
}

export default function ServiceShowcase() {

    // Shared window/track refs — only used by the mobile/tablet
    // swipe carousel.
    const windowRef = useRef(null);
    const sliderRef = useRef(null);

    // One ref per desktop group, used by the IntersectionObserver.
    const groupRefs = useRef([]);

    const stepRef = useRef(0);
    const animatingRef = useRef(false);

    // Mobile/tablet swipe tracking (Instagram-style carousel).
    const mobileDraggingRef = useRef(false);
    const mobileDragIntentRef = useRef(null);
    const mobileDragStartXRef = useRef(0);
    const mobileDragStartYRef = useRef(0);
    const mobileDragStartTimeRef = useRef(0);
    const mobileBaseXRef = useRef(0);

    const [cardsPerPage, setCardsPerPage] = useState(getCardsPerPage);
    const [activeIndex, setActiveIndex] = useState(0);

    const headingRef = useRef(null);

    const isTouchLayout = cardsPerPage <= 3;

    const totalPages = Math.max(
        1,
        Math.ceil(SERVICES.length / cardsPerPage)
    );

    const totalSteps = totalPages - 1;

    // ==========================================================
    // RESIZE
    // ==========================================================

    useEffect(() => {

        let resizeTimer = null;

        const handleResize = () => {

            if (resizeTimer) {
                clearTimeout(resizeTimer);
            }

            resizeTimer = setTimeout(() => {

                const next = getCardsPerPage();

                if (next !== cardsPerPage) {

                    setCardsPerPage(next);

                    stepRef.current = 0;
                    animatingRef.current = false;

                    setActiveIndex(0);

                    if (sliderRef.current) {
                        gsap.set(sliderRef.current, { x: 0 });
                    }
                }

            }, 120);
        };

        window.addEventListener("resize", handleResize);

        return () => {

            if (resizeTimer) {
                clearTimeout(resizeTimer);
            }

            window.removeEventListener("resize", handleResize);
        };

    }, [cardsPerPage]);

    // ==========================================================
    // MOBILE / TABLET SWIPE CAROUSEL
    // Unchanged mechanics — normal horizontal carousel driven
    // entirely by touch gestures, independent of page scroll.
    // ==========================================================

    const goToTouchStep = useCallback((targetStep, { instant = false } = {}) => {

        if (!isTouchLayout) return;

        const windowElement = windowRef.current;
        const track = sliderRef.current;

        if (!windowElement || !track) return;

        const clampedStep = Math.max(
            0,
            Math.min(targetStep, totalSteps)
        );

        const targetX = -clampedStep * windowElement.clientWidth;

        if (instant) {

            gsap.set(track, { x: targetX });

            stepRef.current = clampedStep;
            setActiveIndex(clampedStep);

            return;
        }

        if (animatingRef.current) return;

        animatingRef.current = true;

        gsap.to(track, {

            x: targetX,
            duration: 0.45,
            ease: "power3.out",

            onComplete: () => {

                stepRef.current = clampedStep;
                setActiveIndex(clampedStep);
                animatingRef.current = false;
            },
        });

    }, [isTouchLayout, totalSteps]);

    useLayoutEffect(() => {

        if (!isTouchLayout) return;

        const track = sliderRef.current;
        if (!track) return;

        stepRef.current = 0;
        animatingRef.current = false;

        setActiveIndex(0);

        gsap.set(track, { x: 0 });

    }, [isTouchLayout, cardsPerPage]);

    useEffect(() => {

        if (!isTouchLayout) return;

        const windowElement = windowRef.current;
        const track = sliderRef.current;

        if (!windowElement || !track) return;

        const SWIPE_DISTANCE = 60;
        const SWIPE_VELOCITY = 0.35;
        const INTENT_THRESHOLD = 8;
        const RUBBER_BAND = 0.35;

        const handleTouchStart = (event) => {

            if (animatingRef.current) return;

            const touch = event.touches[0];

            mobileDraggingRef.current = true;
            mobileDragIntentRef.current = null;

            mobileDragStartXRef.current = touch.clientX;
            mobileDragStartYRef.current = touch.clientY;
            mobileDragStartTimeRef.current = Date.now();

            mobileBaseXRef.current =
                -stepRef.current * windowElement.clientWidth;
        };

        const handleTouchMove = (event) => {

            if (!mobileDraggingRef.current) return;

            const touch = event.touches[0];

            const deltaX = touch.clientX - mobileDragStartXRef.current;
            const deltaY = touch.clientY - mobileDragStartYRef.current;

            if (!mobileDragIntentRef.current) {

                if (
                    Math.abs(deltaX) < INTENT_THRESHOLD &&
                    Math.abs(deltaY) < INTENT_THRESHOLD
                ) {
                    return;
                }

                mobileDragIntentRef.current =
                    Math.abs(deltaX) > Math.abs(deltaY)
                        ? "horizontal"
                        : "vertical";
            }

            if (mobileDragIntentRef.current === "vertical") return;

            event.preventDefault();

            let rawX = mobileBaseXRef.current + deltaX;

            const minX = -totalSteps * windowElement.clientWidth;
            const maxX = 0;

            if (rawX > maxX) {
                rawX = maxX + (rawX - maxX) * RUBBER_BAND;
            } else if (rawX < minX) {
                rawX = minX + (rawX - minX) * RUBBER_BAND;
            }

            gsap.set(track, { x: rawX });
        };

        const handleTouchEnd = (event) => {

            if (!mobileDraggingRef.current) return;

            mobileDraggingRef.current = false;

            if (mobileDragIntentRef.current !== "horizontal") {
                mobileDragIntentRef.current = null;
                return;
            }

            const touch = event.changedTouches[0];

            const deltaX = touch.clientX - mobileDragStartXRef.current;

            const elapsed = Math.max(
                1,
                Date.now() - mobileDragStartTimeRef.current
            );

            const velocity = deltaX / elapsed;

            let targetStep = stepRef.current;

            if (
                deltaX <= -SWIPE_DISTANCE ||
                velocity <= -SWIPE_VELOCITY
            ) {
                targetStep = stepRef.current + 1;
            } else if (
                deltaX >= SWIPE_DISTANCE ||
                velocity >= SWIPE_VELOCITY
            ) {
                targetStep = stepRef.current - 1;
            }

            targetStep = Math.max(
                0,
                Math.min(targetStep, totalSteps)
            );

            goToTouchStep(targetStep);

            mobileDragIntentRef.current = null;
        };

        const handleTouchCancel = () => {

            if (!mobileDraggingRef.current) return;

            mobileDraggingRef.current = false;
            mobileDragIntentRef.current = null;

            goToTouchStep(stepRef.current);
        };

        windowElement.addEventListener("touchstart", handleTouchStart, { passive: true });
        windowElement.addEventListener("touchmove", handleTouchMove, { passive: false });
        windowElement.addEventListener("touchend", handleTouchEnd, { passive: true });
        windowElement.addEventListener("touchcancel", handleTouchCancel, { passive: true });

        return () => {

            windowElement.removeEventListener("touchstart", handleTouchStart);
            windowElement.removeEventListener("touchmove", handleTouchMove);
            windowElement.removeEventListener("touchend", handleTouchEnd);
            windowElement.removeEventListener("touchcancel", handleTouchCancel);
        };

    }, [isTouchLayout, totalSteps, goToTouchStep]);

    // ==========================================================
    // DESKTOP REVEAL ANIMATION
    // Groups sit in normal document flow. An IntersectionObserver
    // (never touches scroll, never calls preventDefault) reveals
    // each group once as it enters the viewport, alternating
    // entrance direction by group index, then leaves the cards at
    // their normal transform/opacity for good.
    // ==========================================================

    useLayoutEffect(() => {

        if (isTouchLayout) return;

        groupRefs.current.forEach((group, index) => {

            if (!group) return;

            const direction = index % 2 === 0 ? 1 : -1;

            // Train logic: the whole row of 5 cards moves as one
            // connected unit, fully off-screen — not each card
            // fading in on its own.
            gsap.set(group, {
                xPercent: direction * 100,
            });
        });

    }, [isTouchLayout, cardsPerPage, totalPages]);

    useEffect(() => {

        if (isTouchLayout) return;

        const groups = groupRefs.current.filter(Boolean);

        if (groups.length === 0) return;

        const revealed = new Set();

        const observer = new IntersectionObserver(

            (entries) => {

                entries.forEach((entry) => {

                    const index = Number(entry.target.dataset.groupIndex);

                    if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
                        setActiveIndex(index);
                    }

                    if (entry.isIntersecting && !revealed.has(index)) {

                        revealed.add(index);

                        // Train logic: the row slides in as one rigid
                        // block and settles — no stagger, no fade.
                        gsap.to(entry.target, {
                            xPercent: 0,
                            duration: 1,
                            ease: "power3.out",
                        });
                    }
                });
            },

            {
                threshold: [0, 0.2, 0.45, 0.8],
                rootMargin: "0px 0px -10% 0px",
            }
        );

        groups.forEach((group) => observer.observe(group));

        return () => observer.disconnect();

    }, [isTouchLayout, cardsPerPage, totalPages]);

    // ==========================================================
    // NAVIGATION
    // On desktop, arrows and pagination dots are purely visual
    // indicators of which group is currently in view — the user
    // scrolls manually, nothing here moves the page for them.
    // Only the mobile/tablet swipe carousel is interactive.
    // ==========================================================

    const nextPage = () => {

        if (!isTouchLayout) return;

        goToTouchStep(stepRef.current + 1);
    };

    const prevPage = () => {

        if (!isTouchLayout) return;

        goToTouchStep(stepRef.current - 1);
    };

    const gotoPage = (index) => {

        if (!isTouchLayout) return;

        goToTouchStep(index);
    };

    // ==========================================================
    // HEADING ANIMATION
    // ==========================================================

    useLayoutEffect(() => {

        const heading = headingRef.current;

        if (!heading) return;

        gsap.set(heading, {
            y: 35,
            opacity: 0,
        });

        const observer = new IntersectionObserver(
            ([entry]) => {

                if (entry.isIntersecting) {

                    gsap.to(heading, {
                        y: 0,
                        opacity: 1,
                        duration: 0.9,
                        ease: "power3.out",
                    });

                    observer.disconnect();
                }

            },
            {
                threshold: 0.3,
            }
        );

        observer.observe(heading);

        return () => observer.disconnect();

    }, []);

    // ==========================================================
    // RENDER
    // ==========================================================

    return (

        <section className="services-showcase">

            <div className="services-header">

                <div>

                    <motion.p
                        className="services-tag"
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                            duration: 0.9,
                            ease: [0.22, 1, 0.36, 1]
                        }}
                    >
                        OUR SERVICES
                    </motion.p>

                    <motion.h2
                        className="services-title"
                        initial={{ opacity: 0, y: 60, scale: 0.98 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{
                            duration: 1,
                            ease: [0.22, 1, 0.36, 1],
                            delay: 0.15
                        }}
                    >
                        Engineering Solutions
                    </motion.h2>

                </div>

                <div className="services-arrows">

                    <button onClick={prevPage} aria-label="Previous services">
                        <ChevronLeft size={22} />
                    </button>

                    <div className="services-mobile-bars">

                        {Array.from({ length: totalPages }).map((_, index) => (
                            <span
                                key={index}
                                className={activeIndex === index ? "active" : ""}
                            />
                        ))}

                    </div>

                    <button onClick={nextPage} aria-label="Next services">
                        <ChevronRight size={22} />
                    </button>

                </div>

            </div>

            {isTouchLayout ? (

                <div className="services-window" ref={windowRef}>

                    <button
                        className="mobile-arrow mobile-arrow-left"
                        onClick={prevPage}
                        aria-label="Previous services"
                    >
                        <ChevronLeft size={22} />
                    </button>

                    <button
                        className="mobile-arrow mobile-arrow-right"
                        onClick={nextPage}
                        aria-label="Next services"
                    >
                        <ChevronRight size={22} />
                    </button>

                    <div className="services-track" ref={sliderRef}>

                        {Array.from({ length: totalPages }).map((_, pageIndex) => (

                            <div
                                className={`services-page cards-${cardsPerPage}`}
                                key={pageIndex}
                            >

                                {SERVICES
                                    .slice(
                                        pageIndex * cardsPerPage,
                                        pageIndex * cardsPerPage + cardsPerPage
                                    )
                                    .map((service, index) => (
                                        <ServiceCard
                                            key={`${pageIndex}-${index}`}
                                            service={service}
                                        />
                                    ))}

                            </div>
                        ))}

                    </div>

                </div>

            ) : (

                <div className="services-groups">

                    {Array.from({ length: totalPages }).map((_, groupIndex) => (

                        <div
                            key={groupIndex}
                            data-group-index={groupIndex}
                            ref={(el) => { groupRefs.current[groupIndex] = el; }}
                            className={
                                `services-group cards-${cardsPerPage} ` +
                                (groupIndex % 2 === 0
                                    ? "services-group-primary"
                                    : "services-group-alt")
                            }
                        >

                            {SERVICES
                                .slice(
                                    groupIndex * cardsPerPage,
                                    groupIndex * cardsPerPage + cardsPerPage
                                )
                                .map((service, index) => (
                                    <ServiceCard
                                        key={`${groupIndex}-${index}`}
                                        service={service}
                                    />
                                ))}

                        </div>
                    ))}

                </div>
            )}

            <div className="services-pagination">

                {Array.from({ length: totalPages }).map((_, index) => (

                    <button
                        key={index}
                        className={activeIndex === index ? "active" : ""}
                        onClick={() => gotoPage(index)}
                        aria-label={`Go to service group ${index + 1}`}
                    />
                ))}

            </div>

        </section>
    );
}
